import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell, ComposedChart,
} from 'recharts';
import { formatMoney, formatQty, formatPct, formatShortDate } from './shared';

const CHART_COLORS = [
  '#0F766E', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6', '#6366F1', '#84CC16',
];

const shortLabel = (name, max = 22) => {
  if (!name) return '—';
  return name.length > max ? `${name.slice(0, max)}…` : name;
};

const toChartRows = (rows, dataKey) => (
  (rows || []).map((row, index) => ({
    ...row,
    label: shortLabel(row.name),
    fill: CHART_COLORS[index % CHART_COLORS.length],
    metric: row[dataKey] ?? 0,
  }))
);

function KpiCard({ label, value, sub, accent = 'border-teal-500', icon }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
          {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
        </div>
        {icon ? <span className="text-2xl shrink-0">{icon}</span> : null}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, emptyMessage, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {subtitle ? <p className="text-xs text-gray-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      {children || (
        <p className="text-sm text-gray-500 py-16 text-center">{emptyMessage || 'Sin datos en el período.'}</p>
      )}
    </div>
  );
}

function HorizontalRankingChart({
  title,
  subtitle,
  rows,
  dataKey,
  color = '#0F766E',
  formatValue,
  emptyMessage,
}) {
  const chartData = toChartRows(rows, dataKey).reverse();

  return (
    <ChartCard title={title} subtitle={subtitle} emptyMessage={emptyMessage}>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 36)}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 20, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => formatValue(v, true)} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v) => [formatValue(v), '']}
              labelFormatter={(_l, payload) => payload?.[0]?.payload?.name || _l}
            />
            <Bar dataKey="metric" fill={color} radius={[0, 4, 4, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      ) : null}
    </ChartCard>
  );
}

function DualMetricChart({ title, subtitle, rows }) {
  const chartData = (rows || []).map((row) => ({
    ...row,
    label: shortLabel(row.name, 14),
    name: row.name,
  }));

  return (
    <ChartCard title={title} subtitle={subtitle}>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 48 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} />
            <YAxis yAxisId="left" tickFormatter={(v) => formatQty(v, 0)} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}×`} tick={{ fontSize: 11 }} />
            <Tooltip
              labelFormatter={(_l, payload) => payload?.[0]?.payload?.name || _l}
              formatter={(v, key) => {
                if (key === 'rotation_index') return [`${formatQty(v, 2)}×`, 'Rotación'];
                if (key === 'stock_value') return [formatMoney(v), 'Valor stock'];
                return [formatQty(v), key === 'exit_qty' ? 'Salidas' : key];
              }}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="stock_value" name="Valor stock (S/)" fill="#0F766E" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="left" dataKey="exit_qty" name="Salidas período" fill="#93C5FD" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="rotation_index" name="Rotación (×)" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : null}
    </ChartCard>
  );
}

export default function InventoryDashboardView({ data, meta }) {
  if (!data) return null;

  const { kpis } = data;
  const warehousePie = (data.warehouse_distribution || [])
    .filter((w) => (w.stock_value ?? 0) > 0)
    .map((w) => ({
      name: w.warehouse_code || w.warehouse_name,
      value: w.stock_value,
      share: w.value_share_pct,
    }));

  const warehouseValueBars = (data.warehouse_distribution || []).map((w, i) => ({
    ...w,
    label: w.warehouse_code,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const insumosByValue = (data.top_insumos_by_value?.length
    ? data.top_insumos_by_value
    : data.top_products_by_value?.filter((r) => r.exit_qty > 0).slice(0, 10)) || [];

  const insumosByValueSubtitle = data.top_insumos_by_value?.length
    ? 'Mayor valor en inventario hoy'
    : meta?.insumos_value_fallback
      ? 'Productos con mayor valor y movimiento (sin insumos marcados en catálogo)'
      : 'Mayor valor en inventario hoy';

  const inconsistencyPairs = kpis?.kardex_inconsistency_pairs;
  const inconsistencyMovements = kpis?.kardex_inconsistency_movements;
  const hasInconsistencyCounts = inconsistencyPairs != null && inconsistencyMovements != null;
  const entrySpikeCount = kpis?.entry_spike_count ?? data.entry_change_alerts?.length ?? 0;
  const referenceDay = meta?.date_to;

  return (
    <div className="space-y-6">
      {data.entry_change_alerts?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-base font-semibold text-amber-950 flex items-center gap-2">
                <span aria-hidden>📈</span>
                Entradas con mayor cambio
              </h3>
              <p className="text-xs text-amber-900/90 mt-1 max-w-2xl">
                Aumento significativo de entradas respecto al día anterior
                {referenceDay ? ` (${formatShortDate(referenceDay)} vs ${formatShortDate(data.entry_change_alerts[0]?.previous_date)})` : ''}.
                {' '}Ej.: ayer 10 → hoy 50 unidades.
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-amber-200 px-3 py-1 text-xs font-semibold text-amber-950">
              {entrySpikeCount} alerta{entrySpikeCount === 1 ? '' : 's'}
            </span>
            <Link
              to="/reports/entry-change-alerts"
              className="shrink-0 text-xs font-medium text-teal-700 hover:text-teal-900 underline"
            >
              Ver reporte completo →
            </Link>
          </div>
          <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-amber-100/80 text-left text-xs uppercase tracking-wide text-amber-950">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Producto</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Día anterior</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Día actual</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Variación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {data.entry_change_alerts.map((row) => (
                  <tr key={row.product_id} className="hover:bg-amber-50/50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{row.name}</div>
                      {row.sku ? <div className="text-xs text-gray-500">{row.sku}</div> : null}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 whitespace-nowrap">
                      {formatQty(row.previous_qty)}
                      {row.unit_of_measure ? ` ${row.unit_of_measure}` : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-amber-900 whitespace-nowrap">
                      {formatQty(row.current_qty)}
                      {row.unit_of_measure ? ` ${row.unit_of_measure}` : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <span className="font-semibold text-amber-800">
                        +{formatQty(row.change_qty)}
                      </span>
                      {row.change_ratio_pct != null ? (
                        <span className="text-xs text-amber-700 ml-1">({formatPct(row.change_ratio_pct)})</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          label="Valor en inventario"
          value={formatMoney(kpis?.total_stock_value)}
          sub={`${formatQty(kpis?.total_stock_qty)} unidades · ${kpis?.products_with_stock || 0} productos`}
          icon="💰"
        />
        <KpiCard
          label="Almacenes con stock"
          value={kpis?.warehouses_with_stock ?? '—'}
          sub={`${kpis?.insumos_with_stock || 0} insumos con existencias`}
          accent="border-blue-500"
          icon="🏭"
        />
        <KpiCard
          label="Movimientos del período"
          value={`${(kpis?.entries_count || 0) + (kpis?.exits_count || 0)}`}
          sub={`${kpis?.entries_count || 0} entradas · ${kpis?.exits_count || 0} salidas`}
          accent="border-violet-500"
          icon="📦"
        />
        <KpiCard
          label="Índice salidas/entradas"
          value={kpis?.balance_index != null ? formatQty(kpis.balance_index, 2) : '—'}
          sub={kpis?.low_stock_count > 0 ? `${kpis.low_stock_count} bajo mínimo` : 'Flujo del período'}
          accent="border-amber-500"
          icon="⚖️"
        />
        <KpiCard
          label="Picos de entradas"
          value={entrySpikeCount}
          sub={entrySpikeCount > 0 ? 'Aumento vs día anterior' : 'Sin aumentos relevantes'}
          accent={entrySpikeCount > 0 ? 'border-orange-500' : 'border-gray-300'}
          icon={entrySpikeCount > 0 ? '📈' : '—'}
        />
        <Link to="/reports/kardex-inconsistencies" className="block hover:opacity-95 transition-opacity">
          <KpiCard
            label="Inconsistencias Kardex"
            value={hasInconsistencyCounts ? inconsistencyPairs : 'Ver reporte'}
            sub={
              hasInconsistencyCounts
                ? (inconsistencyMovements > 0
                  ? `${inconsistencyMovements} movimientos · Ver detalle`
                  : 'Sin inconsistencias detectadas')
                : 'Abrir auditoría de kardex'
            }
            accent={
              hasInconsistencyCounts && inconsistencyPairs > 0
                ? 'border-red-500'
                : hasInconsistencyCounts
                  ? 'border-green-500'
                  : 'border-gray-300'
            }
            icon={hasInconsistencyCounts && inconsistencyPairs > 0 ? '⚠️' : hasInconsistencyCounts ? '✓' : '→'}
          />
        </Link>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Distribución entre almacenes" subtitle="Participación del valor total en inventario">
          {warehousePie.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={warehousePie}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, share }) => `${name} ${formatPct(share)}`}
                  labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
                >
                  {warehousePie.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, _n, props) => [`${formatMoney(v)} (${formatPct(props.payload.share)})`, 'Valor']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : null}
        </ChartCard>

        <ChartCard title="Valor en stock por almacén" subtitle="Monto total por ubicación">
          {warehouseValueBars.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={warehouseValueBars} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatMoney(v).replace('S/ ', '')} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatMoney(v), 'Valor']} labelFormatter={(l, payload) => payload?.[0]?.payload?.warehouse_name || l} />
                <Bar dataKey="stock_value" name="Valor" radius={[4, 4, 0, 0]}>
                  {warehouseValueBars.map((entry, i) => (
                    <Cell key={entry.warehouse_id} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </ChartCard>
      </div>

      {data.monthly_flow?.length > 0 && (
        <ChartCard title="Entradas vs salidas por mes" subtitle="Cantidad movida en el período seleccionado">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.monthly_flow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatQty(v, 0)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, key) => [formatQty(v), key === 'entries_qty' ? 'Entradas' : 'Salidas']} />
              <Legend />
              <Line type="monotone" dataKey="entries_qty" name="Entradas" stroke="#0F766E" strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="exits_qty" name="Salidas" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Salidas por tipo de evento" subtitle="Distribución del consumo y movimientos">
          {data.exits_by_event?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.exits_by_event} margin={{ top: 8, right: 12, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v) => formatQty(v, 0)} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [formatQty(v), 'Cantidad']} />
                <Bar dataKey="qty" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Cantidad" />
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </ChartCard>

        <ChartCard title="Flujo monetario mensual" subtitle="Valor de entradas y salidas (S/)">
          {data.monthly_flow?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly_flow} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => formatMoney(v).replace('S/ ', '')} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, key) => [formatMoney(v), key === 'entries_value' ? 'Entradas' : 'Salidas']} />
                <Legend />
                <Bar dataKey="entries_value" name="Entradas S/" fill="#0F766E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="exits_value" name="Salidas S/" fill="#F87171" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </ChartCard>
      </div>

      {data.insumo_warehouse_distribution?.length > 0 && (
        <ChartCard title="Reparto de insumos clave entre almacenes" subtitle="Top 5 por valor — % por ubicación">
          <div className="space-y-5">
            {data.insumo_warehouse_distribution.map((item, idx) => (
              <div key={item.product_id}>
                <div className="flex justify-between gap-2 mb-1.5">
                  <span className="font-medium text-sm text-gray-800">{item.name}</span>
                  <span className="text-sm font-semibold text-teal-700">{formatMoney(item.total_value)}</span>
                </div>
                <div className="flex h-7 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                  {item.warehouses.map((wh, i) => (
                    <div
                      key={wh.warehouse_id}
                      title={`${wh.warehouse_name}: ${formatPct(wh.share_pct)}`}
                      className="flex items-center justify-center text-[10px] font-medium text-white transition-all"
                      style={{
                        width: `${Math.max(wh.share_pct, 4)}%`,
                        backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                      }}
                    >
                      {wh.share_pct >= 12 ? `${wh.warehouse_code} ${formatPct(wh.share_pct)}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <HorizontalRankingChart
          title="Mayor rotación de inventario"
          subtitle="Índice anualizado: salidas del período ÷ stock actual"
          rows={data.rotation_ranking}
          dataKey="rotation_index"
          color="#0F766E"
          formatValue={(v, axis) => (axis ? `${formatQty(v, 0)}×` : `${formatQty(v, 2)}×`)}
        />
        <HorizontalRankingChart
          title="Insumos con más salidas"
          subtitle="Cantidad consumida o salida en el período"
          rows={data.top_insumos_by_exits?.length ? data.top_insumos_by_exits : data.rotation_ranking}
          dataKey="exit_qty"
          color="#3B82F6"
          formatValue={(v) => formatQty(v)}
          emptyMessage="Sin salidas registradas en el período."
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <HorizontalRankingChart
          title="Insumos más importantes (por valor)"
          subtitle={insumosByValueSubtitle}
          rows={insumosByValue}
          dataKey="stock_value"
          color="#8B5CF6"
          formatValue={(v, axis) => (axis ? formatMoney(v).replace('S/ ', '') : formatMoney(v))}
        />
        <HorizontalRankingChart
          title="Productos de mayor valor en stock"
          subtitle="Top 10 por valor monetario en inventario"
          rows={data.top_products_by_value}
          dataKey="stock_value"
          color="#F59E0B"
          formatValue={(v, axis) => (axis ? formatMoney(v).replace('S/ ', '') : formatMoney(v))}
        />
      </div>

      {data.top_products_by_value?.length > 0 && (
        <DualMetricChart
          title="Valor, salidas y rotación — productos clave"
          subtitle="Vista combinada de los productos con mayor impacto en inventario"
          rows={data.top_products_by_value?.slice(0, 8)}
        />
      )}

      {data.low_stock_alerts?.length > 0 && (
        <HorizontalRankingChart
          title="⚠️ Alertas de bajo stock mínimo"
          subtitle="Productos por debajo del mínimo configurado"
          rows={data.low_stock_alerts}
          dataKey="gap_qty"
          color="#EF4444"
          formatValue={(v) => formatQty(v)}
        />
      )}
    </div>
  );
}
